// =========================================================================
// useSourceDrawer — open the `oem-source` drawer on a dataset slice.
// Issues module, fork-owned (zone C).
//
// The source demo's figures are `<button class="fig">` elements whose click
// runs `audit(srcKey, filter)` synchronously against an in-memory bundle. The
// port fetches the domain file on demand, so the click is async: this hook
// owns that await, the pending flag while it runs, and the fallback when a
// dataset key has no ported spec (the drawer then shows the raw reference
// rather than opening empty).
//
// Options carry the rest of the demo's `audit(key, filter, opts)`: a filter
// that can be a list or a range as well as a value, rows to highlight, and a
// `calc` note for the "How this figure is calculated" box.
//
// Engine layer — orchestrates state, so it is a hook rather than a transform.
// =========================================================================

import { useCallback, useState } from 'react';
import { useDrawer } from '@/app/providers';
import type { SourceCalc } from '@/versions/v1/chrome-oem/SourceDrawerBody';
import type { RowFilter } from '@/versions/v1/shared/row-filter';
import { datasetTitle, loadDatasetRows } from './dataset-source';

export type OpenDatasetOptions = {
  /** Overrides the demo's dataset title on the drawer header. */
  readonly title?: string;
  /**
   * Rows kept, e.g. `{ machine_id: 'T-09' }`, `{ machine_id: ['T-09', 'T-11'] }`
   * or `{ date: { between: ['2026-08-31', '2026-09-06'] } }`.
   */
  readonly filter?: RowFilter;
  /** Rows marked among those kept — the demo's `opts.hl`. */
  readonly highlight?: RowFilter;
  /** The "How this figure is calculated" note — the demo's `opts.calc`. */
  readonly calc?: SourceCalc;
};

export type SourceDrawerApi = {
  /** Fetch the slice and open the drawer on it. */
  openDataset: (dataset: string, options?: OpenDatasetOptions) => void;
  /** The dataset key currently being fetched, or `null`. */
  pending: string | null;
};

export function useSourceDrawer(): SourceDrawerApi {
  const { open } = useDrawer();
  const [pending, setPending] = useState<string | null>(null);

  const openDataset = useCallback(
    (dataset: string, options: OpenDatasetOptions = {}) => {
      setPending(dataset);
      loadDatasetRows(dataset, { filter: options.filter, highlight: options.highlight })
        .then((payload) => {
          if (payload) {
            const title = options.title ?? payload.title;
            open('oem-source', {
              wide: true,
              title,
              data: { ...payload, title, ...(options.calc ? { calc: options.calc } : {}) },
            });
            return;
          }
          // No ported spec for this key — say so plainly and keep the raw
          // reference visible so the citation is still traceable by hand.
          open('oem-source', {
            title: datasetTitle(dataset),
            data: {
              kind: 'table',
              title: datasetTitle(dataset),
              columns: ['reference'],
              rows: [[dataset]],
              provenance: `reference only · this dataset is not part of the Tier 1 port`,
            },
          });
        })
        .catch((error: unknown) => {
          open('oem-source', {
            title: datasetTitle(dataset),
            data: {
              kind: 'table',
              title: datasetTitle(dataset),
              columns: ['reference', 'error'],
              rows: [[dataset, error instanceof Error ? error.message : String(error)]],
              provenance: 'source could not be loaded',
            },
          });
        })
        .finally(() => setPending(null));
    },
    [open],
  );

  return { openDataset, pending };
}
